import { Permission } from '@app/Permission';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { EnumVendedorStatus } from '@model/Vendedor';
import React from 'react';

export enum EnumTheme {
    Default = 1,
    Pink = 2,
    Purple = 3
}

export enum EnumTipoUsuario {
    None = "0",
    Interno = 1,
    Externo = 2
}

interface IComboScheme {
    vendedorStatus: EnumVendedorStatus[]
}

export interface IUser {
    displayName: string
    type: EnumTipoUsuario
    permissions: Permission[]
    combos: IComboScheme
}

export interface IAuthState {
    themeName?: EnumTheme
    token?: string
    user?: IUser
}

export interface IAuth {
    state?: IAuthState,
    setState: (newState: IAuthState) => void,
    clearState: VoidFunction,
    clearAuth: VoidFunction,

    hasAuth: boolean,
    hasPermission: (permission: Permission) => boolean
}

const AuthContext = React.createContext<IAuth | undefined>(undefined);
const userKey = "user";

export function useAuth () : IAuth {
    const localStorage = useLocalStorage();

    const localState = localStorage.getObject<IAuthState>(userKey) || undefined;
    
    const [state, _setState] = React.useState<IAuthState | undefined>(localState);

    const setState = React.useCallback((newState: IAuthState) => {
        _setState(newState);
        localStorage.setObject(userKey, newState);
    }, [localStorage]);

    const clearState = React.useCallback(() => {
        _setState(undefined);
        localStorage.clear();
    }, [localStorage]);

    const clearAuth = React.useCallback(() => {
        _setState(s => ({
            ...s,
            token: undefined,
            user: undefined
        }));

        localStorage.remove(userKey);

    }, [localStorage]);

    const hasPermission = React.useCallback((permission: Permission | Permission[]) => {
        if (Array.isArray(permission)) {
            for (let i = 0; i < permission.length; i++) {
                const result = state?.user?.permissions.indexOf(permission[i]) !== -1;

                if (result) {
                    return true;
                }
            }

            return false;
        }
        else {
            return state?.user?.permissions.indexOf(permission) !== -1;
        }
    }, [state?.user?.permissions]);

    return React.useMemo(() => ({
        state,
        setState,
        clearState,
        clearAuth,

        hasAuth: !!state?.token,
        hasPermission

    }), [state, setState, clearState, clearAuth, hasPermission]);
}

export const AuthProvider: React.FC = ({ children }) => {
    const auth = useAuth();
    return <AuthContext.Provider value={auth} children={children} />
}

export function useAuthProvider () {
    return React.useContext(AuthContext)!;
}